import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    capitalize,
    Chip,
    CircularProgress,
    Collapse,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import dayjs, { Dayjs } from "dayjs";
import { ArrowForward, KeyboardArrowDown, SwapVert } from "@mui/icons-material";
import { getNeonColor, scrollable } from "../../theme";
import { useThemeContext } from "../../themeContext";
import { SCALE_STOPS, paletteWinLoss, scaleColor as colorForScale, scaleGlowSx, shadeHex } from "../../settings/numberScale";
import { useReferentialContext } from "../../Context";
import { Maps, ScrimGames, ScrimGameSummaries, ScrimTeamRoundSummaries } from "../../reckon-api";
import { NeonCard, NeonCardContent, NeonCardHeader } from "../../NeonComponents/NeonCards";
import { NeonButton } from "../../NeonComponents/Buttons";
import formatters from "../../NeonComponents/Formatters";
import AnimatedNumber from "../../NeonComponents/Animations/Number";
import TeamIcon from "../../components/Icons/TeamIcon";
import AgentIcon from "../../components/Icons/AgentIcon";
import PlayedAgents, {
    PLAYED_AGENT_ZOOM,
    playedAgentsColumnWidth,
} from "../../components/Icons/PlayedAgents";
import RoundEndIcon from "../../components/Icons/RoundEndIcon";
import WindowContainer from "../../components/layout/WindowContainer";
import DatePicker from "../../components/selectors/Shared/DateSelect";
import { getMapAsset } from "../../valorant-api/mapAssets";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NormalizedGame {
    id: string;
    date: string;
    map: string;
    myScore: number;
    enemyScore: number;
    won: boolean;
    opponent: string;
}

interface GameRoundBreakdown {
    atkWon: number;
    atkLost: number;
    defWon: number;
    defLost: number;
    /** Always 2 slots: first pistol, second pistol. null = unknown/missing */
    pistolResults: [boolean | null, boolean | null];
}

interface MapRateStat {
    rate: number | null;
    wins: number | null;
    losses: number | null;
}

interface MapStats {
    map: string;
    games: number;
    wins: number;
    losses: number;
    winRate: number;
    round: MapRateStat;
    pistol: MapRateStat;
    atk: MapRateStat;
    def: MapRateStat;
}

interface PlayerAgg {
    player: string;
    igName: string;
    games: number;
    wins: number;
    kills: number;
    deaths: number;
    assists: number;
    acs: number;
    adr: number;
    kast: number;
    rating: number;
    hs: number;
    firstKills: number;
    firstDeaths: number;
    agents: Map<string, number>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function teamLabel(teamId: string) {
    return teamId?.split("_")[0] || teamId;
}

function groupGamesByDay(games: NormalizedGame[]) {
    const groups: { dayKey: string; games: NormalizedGame[] }[] = [];
    for (const game of games) {
        const dayKey = dayjs(game.date).format("YYYY-MM-DD");
        const last = groups[groups.length - 1];
        if (last && last.dayKey === dayKey) last.games.push(game);
        else groups.push({ dayKey, games: [game] });
    }
    return groups;
}

function matchesSelectedMap(game: NormalizedGame, selectedMap: string | null) {
    if (!selectedMap) return true;
    return game.map.toLowerCase() === selectedMap.toLowerCase();
}

function buildGameRoundBreakdowns(
    rounds: ScrimTeamRoundSummaries[],
    userTeam: string,
): Map<string, GameRoundBreakdown> {
    const byGame = new Map<string, ScrimTeamRoundSummaries[]>();
    for (const r of rounds) {
        // Prefer team1 match; fall back to players string like history view
        const isMine =
            r.team1 === userTeam ||
            (r.players || "").toLowerCase().includes(teamLabel(userTeam).toLowerCase());
        if (!isMine) continue;
        const list = byGame.get(r.game) || [];
        list.push(r);
        byGame.set(r.game, list);
    }

    const out = new Map<string, GameRoundBreakdown>();
    for (const [gameId, gameRounds] of byGame) {
        const sorted = [...gameRounds].sort((a, b) => a.round_index - b.round_index);
        let atkWon = 0;
        let atkLost = 0;
        let defWon = 0;
        let defLost = 0;
        let firstPistol: boolean | null = null;
        let secondPistol: boolean | null = null;

        for (const r of sorted) {
            const won = r.win === 1;
            if (r.side === "Attacker") {
                if (won) atkWon += 1;
                else atkLost += 1;
            } else if (r.side === "Defender") {
                if (won) defWon += 1;
                else defLost += 1;
            }

            const idx = r.round_index;
            const eco = `${r.round_eco_type_no_bonus || ""} ${r.round_eco_type || ""}`.toLowerCase();
            const isPistolEco = eco.includes("pistol");
            // Support both 0-based (0,12) and 1-based (1,13) round indexes
            if (idx === 0 || idx === 1 || (isPistolEco && firstPistol === null && idx < 2)) {
                firstPistol = won;
            } else if (idx === 12 || idx === 13 || (isPistolEco && idx >= 12 && idx <= 14)) {
                secondPistol = won;
            }
        }

        out.set(gameId, {
            atkWon,
            atkLost,
            defWon,
            defLost,
            pistolResults: [firstPistol, secondPistol],
        });
    }
    return out;
}

function normalizeGames(scrims: ScrimGames[], userTeam: string): NormalizedGame[] {
    return scrims
        .filter((g) => g.team1 === userTeam || g.team2 === userTeam)
        .map((g) => {
            const isTeam1 = g.team1 === userTeam;
            return {
                id: g.id,
                date: g.date,
                map: g.map,
                myScore: isTeam1 ? g.score_team1 : g.score_team2,
                enemyScore: isTeam1 ? g.score_team2 : g.score_team1,
                won: g.winner === userTeam,
                opponent: isTeam1 ? g.team2 : g.team1,
            };
        })
        .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
}

const EMPTY_RATE: MapRateStat = { rate: null, wins: null, losses: null };

function rateFromRecord(wins: number, losses: number): MapRateStat {
    const total = wins + losses;
    if (total <= 0) return EMPTY_RATE;
    return { rate: wins / total, wins, losses };
}

function buildMapStats(games: NormalizedGame[]): MapStats[] {
    const byMap = new Map<string, { games: number; wins: number; roundWins: number; roundLosses: number }>();
    for (const g of games) {
        const cur = byMap.get(g.map) || { games: 0, wins: 0, roundWins: 0, roundLosses: 0 };
        cur.games += 1;
        if (g.won) cur.wins += 1;
        cur.roundWins += g.myScore;
        cur.roundLosses += g.enemyScore;
        byMap.set(g.map, cur);
    }
    return Array.from(byMap.entries())
        .map(([map, s]) => ({
            map,
            games: s.games,
            wins: s.wins,
            losses: s.games - s.wins,
            winRate: s.games ? s.wins / s.games : 0,
            round: rateFromRecord(s.roundWins, s.roundLosses),
            pistol: EMPTY_RATE,
            atk: EMPTY_RATE,
            def: EMPTY_RATE,
        }))
        .sort((a, b) => b.games - a.games || b.winRate - a.winRate);
}

function mapRateStatsFromMetric(rows: any[], rateKey: string, countKeys: string[] = []): Map<string, MapRateStat> {
    const out = new Map<string, MapRateStat>();
    if (!rateKey) return out;
    for (const row of rows) {
        const map = row?.map ?? row?.id;
        const rateRaw = row?.[rateKey] ?? row?.[`${rateKey}__avg`] ?? (rateKey === "win__avg" ? row?.win : undefined);
        if (!map || rateRaw === null || rateRaw === undefined || Number.isNaN(Number(rateRaw))) continue;
        let rate = Number(rateRaw);
        if (rate > 1) rate = rate / 100;
        const rounds = countKeys.reduce((n, key) => n || Number(row?.[key]) || 0, 0);
        const wins = rounds > 0 ? Math.round(rate * rounds) : null;
        const losses = wins === null ? null : Math.max(0, rounds - wins);
        out.set(String(map).toLowerCase(), { rate, wins, losses });
    }
    return out;
}

function mapRateStatsFromRows(rows: any[]): Map<string, MapRateStat> {
    return mapRateStatsFromMetric(rows, "win__avg", ["id__cnt", "rounds"]);
}

function mergeMapRoundRates(
    base: MapStats[],
    pistol: Map<string, MapRateStat>,
    atk: Map<string, MapRateStat>,
    def: Map<string, MapRateStat>,
): MapStats[] {
    return base.map((m) => {
        const key = m.map.toLowerCase();
        return {
            ...m,
            pistol: pistol.get(key) ?? EMPTY_RATE,
            atk: atk.get(key) ?? EMPTY_RATE,
            def: def.get(key) ?? EMPTY_RATE,
        };
    });
}

function presentNumber(value: number | null | undefined): value is number {
    return value != null && !Number.isNaN(Number(value));
}

function avgPresent(sum: number, count: number) {
    return count ? sum / count : 0;
}

function aggregatePlayers(
    summaries: ScrimGameSummaries[],
    gamesById: Map<string, NormalizedGame>,
): PlayerAgg[] {
    const byPlayer = new Map<
        string,
        PlayerAgg & { acsN: number; adrN: number; kastN: number; ratingN: number; hsN: number }
    >();

    for (const s of summaries) {
        const game = gamesById.get(s.game_id);
        if (!game) continue;
        const onMyTeam = s.win === game.won;
        if (!onMyTeam) continue;

        // Group by linked player id — never by riot account / ig_name
        const playerId = s.player;
        if (!playerId) continue;

        let agg = byPlayer.get(playerId);
        if (!agg) {
            agg = {
                player: playerId,
                igName: s.ig_name,
                games: 0,
                wins: 0,
                kills: 0,
                deaths: 0,
                assists: 0,
                acs: 0,
                adr: 0,
                kast: 0,
                rating: 0,
                hs: 0,
                firstKills: 0,
                firstDeaths: 0,
                agents: new Map(),
                acsN: 0,
                adrN: 0,
                kastN: 0,
                ratingN: 0,
                hsN: 0,
            };
            byPlayer.set(playerId, agg);
        }

        agg.games += 1;
        if (s.win) agg.wins += 1;
        agg.kills += s.kills ?? 0;
        agg.deaths += s.deaths ?? 0;
        agg.assists += s.assists ?? 0;
        if (presentNumber(s.acs)) {
            agg.acs += s.acs;
            agg.acsN += 1;
        }
        if (presentNumber(s.adr)) {
            agg.adr += s.adr;
            agg.adrN += 1;
        }
        if (presentNumber(s.kast)) {
            agg.kast += s.kast;
            agg.kastN += 1;
        }
        if (presentNumber(s.rating_vlr) && s.rating_vlr > 0) {
            agg.rating += s.rating_vlr;
            agg.ratingN += 1;
        }
        if (presentNumber(s.headshot_percent)) {
            agg.hs += s.headshot_percent;
            agg.hsN += 1;
        }
        agg.firstKills += s.first_kills ?? 0;
        agg.firstDeaths += s.first_deaths ?? 0;
        if (s.agent) agg.agents.set(s.agent, (agg.agents.get(s.agent) || 0) + 1);
        if (s.ig_name) agg.igName = s.ig_name;
    }

    return Array.from(byPlayer.values())
        .map((p) => ({
            player: p.player,
            igName: p.igName,
            games: p.games,
            wins: p.wins,
            kills: p.kills,
            deaths: p.deaths,
            assists: p.assists,
            acs: avgPresent(p.acs, p.acsN),
            adr: avgPresent(p.adr, p.adrN),
            kast: avgPresent(p.kast, p.kastN),
            rating: avgPresent(p.rating, p.ratingN),
            hs: avgPresent(p.hs, p.hsN),
            firstKills: p.firstKills,
            firstDeaths: p.firstDeaths,
            agents: p.agents,
        }))
        .sort((a, b) => b.rating - a.rating || b.acs - a.acs);
}

function topAgents(agents: Map<string, number>, n = 3): string[] {
    return Array.from(agents.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, n)
        .map(([agent]) => agent);
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function useScale() {
    const { numberScale } = useThemeContext();
    const { win, loss } = paletteWinLoss(numberScale);
    return {
        color: (value: number | null, stops: number[]) => colorForScale(value, stops, numberScale),
        glow: (color: string) => scaleGlowSx(color, numberScale.glow),
        glowAmount: numberScale.glow,
        win,
        loss,
        winWash: shadeHex(win, 0.12),
        lossWash: shadeHex(loss, 0.12),
    };
}

function SideScoreChip({
    label,
    won,
    lost,
}: {
    label: string;
    won: number;
    lost: number;
}) {
    const scale = useScale();
    return (
        <Stack
            direction="row"
            spacing={0.4}
            sx={{
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: 22,
                px: 0.5,
                borderRadius: 0.75,
                backgroundColor: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(255,255,255,0.12)",
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    color: "rgba(255,255,255,0.55)",
                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    width: 24,
                    flexShrink: 0,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    color: scale.win,
                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    fontVariantNumeric: "tabular-nums",
                    minWidth: 14,
                    textAlign: "right",
                }}
            >
                {won}
            </Typography>
            <Typography sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.4)" }}>-</Typography>
            <Typography
                sx={{
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    color: scale.loss,
                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    fontVariantNumeric: "tabular-nums",
                    minWidth: 14,
                    textAlign: "left",
                }}
            >
                {lost}
            </Typography>
        </Stack>
    );
}

function PistolResultsChip({ results }: { results: [boolean | null, boolean | null] }) {
    const scale = useScale();
    return (
        <Stack
            direction="row"
            spacing={0.4}
            sx={{
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: 22,
                px: 0.5,
                borderRadius: 0.75,
                backgroundColor: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(255,255,255,0.12)",
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    color: "rgba(255,255,255,0.55)",
                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    width: 24,
                    flexShrink: 0,
                }}
            >
                PIS
            </Typography>
            {results.map((won, i) => (
                <Box
                    key={i}
                    sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "3px",
                        backgroundColor:
                            won === null
                                ? "rgba(255,255,255,0.08)"
                                : won
                                  ? `${scale.win}59`
                                  : `${scale.loss}59`,
                        border: `1px solid ${
                            won === null
                                ? "rgba(255,255,255,0.2)"
                                : won
                                  ? scale.win
                                  : scale.loss
                        }`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.55rem",
                            fontWeight: 800,
                            color:
                                won === null ? "rgba(255,255,255,0.35)" : won ? scale.win : scale.loss,
                            lineHeight: 1,
                        }}
                    >
                        {won === null ? "·" : won ? "W" : "L"}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
}

const MATCH_DETAILS_HEIGHT = 352;
const SCOREBOARD_COLUMNS = "minmax(88px, 1.8fr) 32px 32px 32px 44px 48px";
const SCOREBOARD_HEADERS = ["Player", "K", "D", "A", "ACS", "RT"];
const SCOREBOARD_AGENT_SLOT = 28;
const MAP_FILTER_TIMEOUT = { enter: 380, exit: 300 };
const MAP_FILTER_EASE = { enter: "cubic-bezier(0.22, 1, 0.36, 1)", exit: "ease-in" };
const MAP_FILTER_MOTION = { duration: 0.36, ease: [0.22, 1, 0.36, 1] as const };

function FilterCollapse({
    in: open,
    children,
    innerSx,
}: {
    in: boolean;
    children: React.ReactNode;
    innerSx?: object;
}) {
    return (
        <Collapse
            in={open}
            timeout={MAP_FILTER_TIMEOUT}
            easing={MAP_FILTER_EASE}
            unmountOnExit={false}
            sx={{ flexShrink: 0 }}
        >
            <Box
                sx={{
                    pb: 0.75,
                    opacity: open ? 1 : 0,
                    transform: open ? "translateY(0)" : "translateY(-8px)",
                    transition: open
                        ? "opacity 280ms cubic-bezier(0.22, 1, 0.36, 1) 40ms, transform 320ms cubic-bezier(0.22, 1, 0.36, 1)"
                        : "opacity 160ms ease-in, transform 220ms ease-in",
                    ...innerSx,
                }}
            >
                {children}
            </Box>
        </Collapse>
    );
}

function DayHeader({ label, neonColor }: { label: string; neonColor: string }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                padding: "8px 12px",
                borderRadius: "6px",
                background: `linear-gradient(90deg, ${neonColor}12 0%, ${neonColor}06 30%, rgba(255,255,255,0.02) 100%)`,
                borderLeft: `3px solid ${neonColor}`,
                boxShadow: `inset 0 0 20px ${neonColor}08`,
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.9)",
                    letterSpacing: "1px",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

function useGameDetails(gameId: string, active: boolean) {
    const ctx = useReferentialContext();
    const loadedFor = useRef<string | null>(null);
    const [players, setPlayers] = useState<ScrimGameSummaries[] | null>(null);
    const [rounds, setRounds] = useState<ScrimTeamRoundSummaries[] | null>(null);
    const [status, setStatus] = useState<"idle" | "loading" | "ready">("idle");

    useEffect(() => {
        if (!active || !gameId) return;
        if (loadedFor.current === gameId) return;
        let cancelled = false;
        setStatus("loading");
        Promise.all([
            ctx.reckonApi.scrimGameSummaries.list({
                additional_filters: { game_id: gameId },
                ordering: ["-acs"],
            }),
            ctx.reckonApi.scrimTeamRoundSummaries.list({
                game: gameId,
                ordering: ["round_index"],
            }),
        ])
            .then(([sums, rnds]) => {
                if (cancelled) return;
                loadedFor.current = gameId;
                setPlayers(sums ?? []);
                setRounds(rnds ?? []);
                setStatus("ready");
            })
            .catch(() => {
                if (cancelled) return;
                loadedFor.current = gameId;
                setPlayers([]);
                setRounds([]);
                setStatus("ready");
            });
        return () => {
            cancelled = true;
        };
    }, [active, gameId]);

    return { players, rounds, status };
}

function allyRoundsForTeam(rounds: ScrimTeamRoundSummaries[], userTeam: string) {
    const needle = teamLabel(userTeam).toLowerCase();
    let mine = rounds
        .filter((r) => r.team1 === userTeam || (r.players || "").toLowerCase().includes(needle))
        .sort((a, b) => a.round_index - b.round_index);
    if (mine.length === 0) {
        mine = rounds.slice(0, Math.floor(rounds.length / 2)).sort((a, b) => a.round_index - b.round_index);
    }
    return mine;
}

function startingSide(rounds: ScrimTeamRoundSummaries[]): "Attacker" | "Defender" | null {
    const pistol = rounds.find((r) => r.round_index === 0 || r.round_index === 1) || rounds[0];
    const side = (pistol?.side || "").toLowerCase();
    if (side.startsWith("attack")) return "Attacker";
    if (side.startsWith("defend")) return "Defender";
    return null;
}

function DetailStatCell({
    value,
    suffix,
    bold,
    digits,
}: {
    value: number | null | undefined;
    suffix?: string;
    bold?: boolean;
    digits?: number;
}) {
    if (value === null || value === undefined) {
        return <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", textAlign: "center" }}>-</Typography>;
    }
    const display = Number.isInteger(value) ? String(value) : value.toFixed(digits ?? (suffix ? 0 : 1));
    return (
        <Typography sx={{ fontSize: "0.72rem", fontWeight: bold ? 800 : 600, color: "rgba(255,255,255,0.9)", textAlign: "center" }}>
            {display}{suffix ?? ""}
        </Typography>
    );
}

function ScoreboardHeader() {
    return (
        <Stack direction="row" sx={{ alignItems: "center" }}>
            <Box sx={{ width: SCOREBOARD_AGENT_SLOT, minWidth: SCOREBOARD_AGENT_SLOT, flexShrink: 0 }} />
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "grid",
                    gridTemplateColumns: SCOREBOARD_COLUMNS,
                    alignItems: "center",
                    columnGap: 0.5,
                    px: 0.75,
                    py: 0.25,
                }}
            >
                {SCOREBOARD_HEADERS.map((h, i) => (
                    <Typography
                        key={`${h}-${i}`}
                        sx={{
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                            color: "rgba(255,255,255,0.4)",
                            textAlign: i === 0 ? "left" : "center",
                        }}
                    >
                        {h}
                    </Typography>
                ))}
            </Box>
        </Stack>
    );
}

function ScoreboardPlayerRow({ row }: { row: ScrimGameSummaries }) {
    const scale = useScale();
    const rating = row.rating_vlr ?? 0;
    return (
        <Stack direction="row" sx={{ alignItems: "center", minWidth: 0 }}>
            <Box sx={{ width: SCOREBOARD_AGENT_SLOT, minWidth: SCOREBOARD_AGENT_SLOT, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                <AgentIcon agent_name={row.agent} sx={{ width: 22, height: 22 }} />
            </Box>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "grid",
                    gridTemplateColumns: SCOREBOARD_COLUMNS,
                    alignItems: "center",
                    columnGap: 0.5,
                    px: 0.75,
                    py: 0.35,
                    borderRadius: 0.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                    }}
                    title={row.ig_name || undefined}
                >
                    {row.player || row.ig_name || "Unknown"}
                </Typography>
                <DetailStatCell value={row.kills} bold />
                <DetailStatCell value={row.deaths} />
                <DetailStatCell value={row.assists} />
                <DetailStatCell value={row.acs} digits={0} />
                <Typography
                    sx={{
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        textAlign: "center",
                        ...scale.glow(scale.color(rating || null, SCALE_STOPS.rating)),
                    }}
                >
                    {row.rating_vlr != null ? row.rating_vlr.toFixed(2) : "-"}
                </Typography>
            </Box>
        </Stack>
    );
}

function TeamBlock({
    title,
    teamName,
    won,
    rows,
}: {
    title: string;
    teamName: string;
    won: boolean;
    rows: ScrimGameSummaries[];
}) {
    const scale = useScale();
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", px: 0.5, mb: 0.35 }}>
                <TeamIcon team_name={teamName} sx={{ width: 18, height: 18 }} />
                <Typography sx={{ fontWeight: 800, fontSize: "0.72rem", letterSpacing: "0.04em" }}>
                    {title}
                </Typography>
                <Chip
                    label={won ? "WIN" : "LOSS"}
                    size="small"
                    sx={{
                        height: 16,
                        fontSize: "0.58rem",
                        fontWeight: 800,
                        backgroundColor: won ? `${scale.win}38` : `${scale.loss}38`,
                        color: won ? scale.win : scale.loss,
                    }}
                />
            </Stack>
            <ScoreboardHeader />
            {rows.map((row) => (
                <ScoreboardPlayerRow key={row.id} row={row} />
            ))}
        </Box>
    );
}

function RoundTimeline({
    rounds,
    userTeam,
}: {
    rounds: ScrimTeamRoundSummaries[];
    userTeam: string;
}) {
    const scale = useScale();
    const allyRounds = allyRoundsForTeam(rounds, userTeam);
    if (allyRounds.length === 0) return null;

    const allyStart = startingSide(allyRounds);
    const firstHalfSide = allyStart === "Attacker" ? "Attackers" : allyStart === "Defender" ? "Defenders" : null;
    const secondHalfSide = firstHalfSide === "Attackers" ? "Defenders" : firstHalfSide === "Defenders" ? "Attackers" : null;
    const hasHalves = allyRounds.length > 12;
    const firstHalfCount = Math.min(allyRounds.length, 12);
    const secondHalfCount = Math.max(allyRounds.length - 12, 0);

    const roundCell = (r: ScrimTeamRoundSummaries, won: boolean) => (
        <Tooltip title={`R${r.round_index + 1} · ${r.side || ""} · ${won ? "Won" : "Lost"}`}>
            <Box
                sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: won ? `${scale.win}38` : `${scale.loss}38`,
                    border: `1.5px solid ${won ? scale.win : scale.loss}80`,
                }}
            >
                <RoundEndIcon round_end={r.round_end ?? undefined} sx={{ width: 15, height: 15 }} />
            </Box>
        </Tooltip>
    );

    return (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `28px repeat(${firstHalfCount}, 22px)${hasHalves ? " 16px" : ""}${secondHalfCount ? ` repeat(${secondHalfCount}, 22px)` : ""}`,
                    gridTemplateRows: firstHalfSide ? "14px 22px 22px" : "22px 22px",
                    columnGap: "4px",
                    rowGap: "4px",
                    alignItems: "center",
                    flexShrink: 0,
                    px: 0.5,
                    mx: "auto",
                    width: "fit-content",
                }}
            >
                {firstHalfSide && (
                    <Typography
                        sx={{
                            gridColumn: `2 / ${firstHalfCount + 2}`,
                            gridRow: 1,
                            fontSize: "0.56rem",
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            color: firstHalfSide === "Attackers" ? "#e57373" : "#80cbc4",
                            textAlign: "center",
                            lineHeight: 1,
                        }}
                    >
                        {firstHalfSide}
                    </Typography>
                )}
                {firstHalfSide && hasHalves && secondHalfSide && (
                    <Typography
                        sx={{
                            gridColumn: `${firstHalfCount + 3} / -1`,
                            gridRow: 1,
                            fontSize: "0.56rem",
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            color: secondHalfSide === "Attackers" ? "#e57373" : "#80cbc4",
                            textAlign: "center",
                            lineHeight: 1,
                        }}
                    >
                        {secondHalfSide}
                    </Typography>
                )}
                <Typography sx={{ gridColumn: 1, gridRow: firstHalfSide ? 2 : 1, fontSize: "0.58rem", fontWeight: 800, color: scale.win, letterSpacing: "0.04em" }}>
                    WIN
                </Typography>
                <Typography sx={{ gridColumn: 1, gridRow: firstHalfSide ? 3 : 2, fontSize: "0.58rem", fontWeight: 800, color: scale.loss, letterSpacing: "0.04em" }}>
                    LOSS
                </Typography>
                {hasHalves && (
                    <Tooltip title="Side swap">
                        <Box sx={{ gridColumn: firstHalfCount + 2, gridRow: firstHalfSide ? "2 / 4" : "1 / 3", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <SwapVert sx={{ fontSize: 18, color: "rgba(255,255,255,0.35)" }} />
                        </Box>
                    </Tooltip>
                )}
                {allyRounds.map((r, i) => {
                    const col = i < 12 ? i + 2 : i + 3;
                    const won = r.win === 1;
                    const row = firstHalfSide ? (won ? 2 : 3) : (won ? 1 : 2);
                    return (
                        <Box key={r.id || `${r.round_index}-${i}`} sx={{ gridColumn: col, gridRow: row, width: 22, height: 22 }}>
                            {roundCell(r, won)}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function GameDetails({
    game,
    userTeam,
    players,
    rounds,
}: {
    game: NormalizedGame;
    userTeam: string;
    players: ScrimGameSummaries[];
    rounds: ScrimTeamRoundSummaries[] | null;
}) {
    if (players.length === 0) {
        return (
            <Typography sx={{ color: "rgba(255,255,255,0.55)", textAlign: "center", py: 2, fontSize: "0.82rem" }}>
                No detailed stats available for this game.
            </Typography>
        );
    }

    const sortByAcs = (rows: ScrimGameSummaries[]) => [...rows].sort((a, b) => (b.acs ?? 0) - (a.acs ?? 0));
    const allies = sortByAcs(players.filter((p) => p.win === game.won));
    const enemies = sortByAcs(players.filter((p) => p.win !== game.won));

    return (
        <Stack spacing={1} sx={{ minWidth: 0, width: "100%" }}>
            {rounds && rounds.length > 0 && <RoundTimeline rounds={rounds} userTeam={userTeam} />}
            <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start", minWidth: 0 }}>
                <TeamBlock title={teamLabel(userTeam)} teamName={userTeam} won={game.won} rows={allies} />
                <TeamBlock title={teamLabel(game.opponent)} teamName={game.opponent} won={!game.won} rows={enemies} />
            </Stack>
        </Stack>
    );
}

function GameRow({
    game,
    userTeam,
    mapsById,
    breakdown,
}: {
    game: NormalizedGame;
    userTeam: string;
    mapsById: Map<string, Maps>;
    breakdown?: GameRoundBreakdown;
}) {
    const [open, setOpen] = useState(false);
    const scale = useScale();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const accent = game.won ? scale.win : scale.loss;
    const wash = game.won ? scale.winWash : scale.lossWash;
    const timeLabel = dayjs(game.date).format("HH:mm");
    const details = useGameDetails(game.id, open);

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                flexShrink: 0,
                borderRadius: 1,
                overflow: "hidden",
                borderLeft: `3px solid ${accent}`,
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    backgroundImage: `url(${mapSplashUrl(game.map, mapsById.get(game.map.toLowerCase()))})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center 35%",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    backgroundColor: shadeHex(accent, 0.42),
                    mixBlendMode: "multiply",
                    opacity: 0.82,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    backgroundColor: accent,
                    mixBlendMode: "soft-light",
                    opacity: 0.28,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background: `linear-gradient(90deg, rgba(0,0,0,0.92) 0%, ${wash}d1 35%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.42) 100%)`,
                    pointerEvents: "none",
                }}
            />

            <Box
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                    position: "relative",
                    zIndex: 2,
                    cursor: "pointer",
                    transition: "filter 0.15s",
                    "&:hover": { filter: "brightness(1.1)" },
                }}
            >
                <Stack
                    direction="row"
                    spacing={1.25}
                    sx={{
                        width: "100%",
                        alignItems: "center",
                        px: 1.5,
                        py: 1.25,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: "rgba(255,255,255,0.7)",
                            minWidth: 40,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                        }}
                    >
                        {timeLabel}
                    </Typography>

                    <TeamIcon team_name={userTeam} sx={{ width: 26, height: 26 }} />
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", minWidth: 52, justifyContent: "center" }}>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: "1.05rem",
                                color: game.won ? scale.win : scale.loss,
                                textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                            }}
                        >
                            {game.myScore}
                        </Typography>
                        <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem" }}>-</Typography>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: "1.05rem",
                                color: game.won ? scale.loss : scale.win,
                                textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                            }}
                        >
                            {game.enemyScore}
                        </Typography>
                    </Stack>
                    <TeamIcon team_name={game.opponent} sx={{ width: 26, height: 26, flexShrink: 0 }} />
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            flex: 1,
                            minWidth: 72,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                        }}
                    >
                        {teamLabel(game.opponent)}
                    </Typography>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "72px 72px 68px 72px",
                            gap: 0.75,
                            alignItems: "center",
                            flexShrink: 0,
                        }}
                    >
                        <SideScoreChip
                            label="ATK"
                            won={breakdown?.atkWon ?? 0}
                            lost={breakdown?.atkLost ?? 0}
                        />
                        <SideScoreChip
                            label="DEF"
                            won={breakdown?.defWon ?? 0}
                            lost={breakdown?.defLost ?? 0}
                        />
                        <PistolResultsChip results={breakdown?.pistolResults ?? [null, null]} />
                        <Chip
                            label={capitalize(game.map)}
                            size="small"
                            variant="outlined"
                            sx={{
                                width: "100%",
                                fontSize: "0.65rem",
                                height: 22,
                                borderColor: game.won ? `${scale.win}8c` : `${scale.loss}8c`,
                                color: game.won ? scale.win : scale.loss,
                                backgroundColor: "rgba(0,0,0,0.45)",
                                backdropFilter: "blur(4px)",
                                "& .MuiChip-label": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    px: 0.75,
                                },
                            }}
                        />
                    </Box>
                    <KeyboardArrowDown
                        sx={{
                            fontSize: 18,
                            color: "rgba(255,255,255,0.55)",
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.25s",
                            flexShrink: 0,
                        }}
                    />
                </Stack>
            </Box>
            <Collapse in={open}>
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        px: 1.25,
                        py: 1.1,
                        minHeight: details.status === "ready" ? undefined : MATCH_DETAILS_HEIGHT,
                        overflow: "auto",
                    }}
                >
                    {details.status === "ready" && details.players ? (
                        <GameDetails
                            game={game}
                            userTeam={userTeam}
                            players={details.players}
                            rounds={details.rounds}
                        />
                    ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: MATCH_DETAILS_HEIGHT }}>
                            <CircularProgress size={24} sx={{ color: neonColor }} />
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}

function mapSplashUrl(mapId: string, mapRef?: Maps) {
    if (mapRef?.uuid) {
        return `https://media.valorant-api.com/maps/${mapRef.uuid}/splash.png`;
    }
    return getMapAsset(mapId, "splash").replace("/upload/", "/upload/c_fill,h_360,w_960/");
}

function mapBannerUrl(mapId: string, mapRef?: Maps) {
    if (mapRef?.uuid) {
        return `https://media.valorant-api.com/maps/${mapRef.uuid}/listviewicon.png`;
    }
    return getMapAsset(mapId, "listViewIcon").replace("/upload/", "/upload/c_fill,h_72,w_200/");
}

function mapGridDims(count: number): { cols: number; rows: number } {
    if (count <= 1) return { cols: 1, rows: 1 };
    // Near-square grid that fits every map (2→2x1, 4→2x2, 5→3x2, 9→3x3, …)
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);
    return { cols, rows };
}

function MapStatCard({
    stats,
    neonColor,
    mapRef,
    selected,
    onSelect,
}: {
    stats: MapStats;
    neonColor: string;
    mapRef?: Maps;
    selected: boolean;
    onSelect: () => void;
}) {
    const scale = useScale();
    const barColor = scale.color(stats.winRate, SCALE_STOPS.rate);
    const [imgSrc, setImgSrc] = useState(mapSplashUrl(stats.map, mapRef));

    useEffect(() => {
        setImgSrc(mapSplashUrl(stats.map, mapRef));
    }, [stats.map, mapRef?.uuid]);

    return (
        <Box
            onClick={onSelect}
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: 0,
                minWidth: 0,
                borderRadius: 1,
                overflow: "hidden",
                cursor: "pointer",
                border: selected ? `2px solid ${neonColor}` : "1px solid rgba(255,255,255,0.12)",
                boxShadow: selected ? `0 0 18px ${neonColor}55, inset 0 0 24px ${neonColor}22` : "none",
                backgroundColor: "rgba(255,255,255,0.03)",
                transition: "border-color 0.2s, box-shadow 0.2s, filter 0.2s",
                "&:hover": {
                    filter: "brightness(1.08)",
                    borderColor: selected ? neonColor : "rgba(255,255,255,0.28)",
                },
            }}
        >
            <Box
                component="img"
                src={imgSrc}
                alt={stats.map}
                onError={() => setImgSrc(mapBannerUrl(stats.map, mapRef))}
                sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0.82) 70%, rgba(0,0,0,0.96) 100%)",
                }}
            />
            <Stack
                sx={{
                    position: "relative",
                    zIndex: 1,
                    height: "100%",
                    justifyContent: "flex-end",
                    p: 0.75,
                    gap: 0.35,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 800,
                        fontSize: "clamp(0.65rem, 1.4vw, 0.9rem)",
                        textTransform: "capitalize",
                        letterSpacing: 0.15,
                        lineHeight: 1.1,
                        textShadow: "0 1px 8px rgba(0,0,0,0.9)",
                    }}
                >
                    {stats.map}
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "56px minmax(56px, 1fr) 48px",
                        columnGap: 0.75,
                        alignItems: "center",
                    }}
                >
                    <Typography sx={{ fontSize: "clamp(0.55rem, 1.1vw, 0.7rem)", color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                        Games
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "clamp(0.55rem, 1.1vw, 0.7rem)",
                            color: "rgba(255,255,255,0.7)",
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            textAlign: "right",
                        }}
                    >
                        {stats.wins}W–{stats.losses}L
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: "clamp(0.6rem, 1.2vw, 0.8rem)",
                            ...scale.glow(barColor),
                            textAlign: "right",
                        }}
                    >
                        {formatters.percent(stats.winRate)}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={Math.max(stats.winRate * 100, 4)}
                    sx={{
                        height: 3,
                        borderRadius: 2,
                        backgroundColor: "rgba(255,255,255,0.12)",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: 2,
                            backgroundColor: barColor,
                            boxShadow: scale.glowAmount ? `0 0 8px ${barColor}88` : "none",
                        },
                    }}
                />
                {(
                    [
                        ["Round", stats.round],
                        ["Pistol", stats.pistol],
                        ["Atk", stats.atk],
                        ["Def", stats.def],
                    ] as const
                ).map(([label, detail]) => {
                    const rate = detail.rate;
                    const color = scale.color(rate, SCALE_STOPS.rate);
                    const record =
                        detail.wins !== null && detail.losses !== null
                            ? `${detail.wins}W–${detail.losses}L`
                            : "—";
                    return (
                        <Box key={label}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "56px minmax(56px, 1fr) 48px",
                                    columnGap: 0.75,
                                    alignItems: "center",
                                }}
                            >
                                <Typography sx={{ fontSize: "clamp(0.55rem, 1.1vw, 0.7rem)", color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                                    {label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "clamp(0.55rem, 1.1vw, 0.7rem)",
                                        color: "rgba(255,255,255,0.55)",
                                        fontWeight: 600,
                                        fontVariantNumeric: "tabular-nums",
                                        textAlign: "right",
                                    }}
                                >
                                    {record}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: "clamp(0.6rem, 1.2vw, 0.8rem)",
                                        ...(rate === null ? { color, fontVariantNumeric: "tabular-nums" } : scale.glow(color)),
                                        textAlign: "right",
                                    }}
                                >
                                    {rate === null ? "—" : formatters.percent(rate)}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={rate === null ? 0 : Math.max(rate * 100, 4)}
                                sx={{
                                    mt: 0.2,
                                    height: 3,
                                    borderRadius: 2,
                                    backgroundColor: "rgba(255,255,255,0.12)",
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 2,
                                        backgroundColor: color,
                                        boxShadow: rate === null || !scale.glowAmount ? "none" : `0 0 8px ${color}88`,
                                    },
                                }}
                            />
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

const PLAYER_STATS_AGENT_SIZE = 44;
const PLAYER_STATS_AGENT_OVERLAP = 20;
const PLAYER_STATS_AGENTS_WIDTH = playedAgentsColumnWidth(
    3,
    PLAYER_STATS_AGENT_SIZE,
    PLAYED_AGENT_ZOOM,
    PLAYER_STATS_AGENT_OVERLAP,
);
const PLAYER_STATS_ROW_HEIGHT = 48;
const PLAYER_STATS_COLUMNS = `minmax(0, 22%) ${PLAYER_STATS_AGENTS_WIDTH}px minmax(0, 8%) minmax(0, 12%) minmax(0, 10%) minmax(0, 12%) minmax(0, 12%) minmax(0, 10%)`;

function PlayerStatsTable({ players }: { players: PlayerAgg[] }) {
    const scale = useScale();
    const headers = ["Player", "Agents", "G", "ACS", "KD", "ADR", "Rating", "FK/FD"];

    const cellSx = {
        px: 0.5,
        fontSize: "0.82rem",
        fontWeight: 600,
        color: "rgba(255,255,255,0.9)",
        display: "flex",
        alignItems: "center",
        minWidth: 0,
    };

    return (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: PLAYER_STATS_COLUMNS,
                    columnGap: 0,
                    px: 0.5,
                    py: 1,
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                }}
            >
                {headers.map((h, i) => (
                    <Typography
                        key={h}
                        sx={{
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.45)",
                            textAlign: i < 2 ? "left" : "center",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {h}
                    </Typography>
                ))}
            </Box>
            <AnimatePresence initial={false}>
                {players.length === 0 ? (
                    <motion.div
                        key="empty-players"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 72, opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={MAP_FILTER_MOTION}
                        style={{ overflow: "hidden" }}
                    >
                        <Typography sx={{ color: "rgba(255,255,255,0.55)", py: 3, textAlign: "center" }}>
                            No player summaries available for these games.
                        </Typography>
                    </motion.div>
                ) : (
                    players.map((player) => {
                        const kd = player.deaths ? player.kills / player.deaths : player.kills;
                        const agents = topAgents(player.agents, 3);
                        const acsColor = scale.color(player.acs, SCALE_STOPS.acs);
                        const kdColor = scale.color(kd, SCALE_STOPS.kd);
                        const adrColor = scale.color(player.adr, SCALE_STOPS.adr);
                        const ratingColor = scale.color(player.rating || null, SCALE_STOPS.rating);

                        return (
                            <motion.div
                                key={player.player}
                                layout
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: PLAYER_STATS_ROW_HEIGHT, opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={MAP_FILTER_MOTION}
                                style={{ overflow: "hidden" }}
                            >
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: PLAYER_STATS_COLUMNS,
                                        height: PLAYER_STATS_ROW_HEIGHT,
                                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                                        "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            ...cellSx,
                                            fontWeight: 700,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            display: "block",
                                            lineHeight: `${PLAYER_STATS_ROW_HEIGHT}px`,
                                        }}
                                        title={player.igName || undefined}
                                    >
                                        {player.player}
                                    </Box>
                                    <Box sx={{ ...cellSx, px: 0, position: "relative", overflow: "hidden" }}>
                                        <PlayedAgents
                                            agents={agents}
                                            size={PLAYER_STATS_AGENT_SIZE}
                                            overlap={PLAYER_STATS_AGENT_OVERLAP}
                                        />
                                    </Box>
                                    <Box sx={{ ...cellSx, justifyContent: "center" }}>
                                        <AnimatedNumber value={player.games} format={formatters.integer} />
                                    </Box>
                                    <Box sx={{ ...cellSx, justifyContent: "center", ...scale.glow(acsColor) }}>
                                        <AnimatedNumber value={player.acs} format={formatters.integer} />
                                    </Box>
                                    <Box sx={{ ...cellSx, justifyContent: "center", ...scale.glow(kdColor) }}>
                                        <AnimatedNumber value={kd} format={formatters.float2} />
                                    </Box>
                                    <Box sx={{ ...cellSx, justifyContent: "center", ...scale.glow(adrColor) }}>
                                        <AnimatedNumber value={player.adr} format={formatters.integer} />
                                    </Box>
                                    <Box sx={{ ...cellSx, justifyContent: "center", fontWeight: 700, ...scale.glow(ratingColor) }}>
                                        {player.rating ? (
                                            <AnimatedNumber value={player.rating} format={formatters.float2} />
                                        ) : (
                                            "—"
                                        )}
                                    </Box>
                                    <Box sx={{ ...cellSx, justifyContent: "center" }}>
                                        <AnimatedNumber value={player.firstKills} format={formatters.integer} />
                                        /
                                        <AnimatedNumber value={player.firstDeaths} format={formatters.integer} />
                                    </Box>
                                </Box>
                            </motion.div>
                        );
                    })
                )}
            </AnimatePresence>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ScrimsDashboard() {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const ctx = useReferentialContext();
    const navigate = useNavigate();
    const userTeam = ctx.user.team;

    const [from, setFrom] = useState(dayjs().subtract(3, "month"));
    const [until, setUntil] = useState(dayjs());
    const [games, setGames] = useState<ScrimGames[]>([]);
    const [summaries, setSummaries] = useState<ScrimGameSummaries[]>([]);
    const [gamesLoading, setGamesLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);
    const [mapRoundRates, setMapRoundRates] = useState<{
        pistol: Map<string, MapRateStat>;
        atk: Map<string, MapRateStat>;
        def: Map<string, MapRateStat>;
    }>({
        pistol: new Map(),
        atk: new Map(),
        def: new Map(),
    });
    const [gameBreakdowns, setGameBreakdowns] = useState<Map<string, GameRoundBreakdown>>(new Map());
    const [selectedMap, setSelectedMap] = useState<string | null>(null);

    useEffect(() => {
        setGamesLoading(true);
        ctx.reckonApi.scrimGames
            .list({
                ordering: ["-date"],
                additional_filters: {
                    date__gte: from.toISOString(),
                    date__lt: until.toISOString(),
                },
            })
            .then((result) => {
                setGames(result);
                setGamesLoading(false);
            })
            .catch(() => setGamesLoading(false));
    }, [from, until]);

    const myGames = useMemo(() => normalizeGames(games, userTeam), [games, userTeam]);
    const baseMapStats = useMemo(() => buildMapStats(myGames), [myGames]);
    const mapStats = useMemo(
        () =>
            mergeMapRoundRates(
                baseMapStats,
                mapRoundRates.pistol,
                mapRoundRates.atk,
                mapRoundRates.def,
            ),
        [baseMapStats, mapRoundRates],
    );
    const mapGrid = useMemo(() => mapGridDims(mapStats.length), [mapStats.length]);
    const gamesById = useMemo(() => new Map(myGames.map((g) => [g.id, g])), [myGames]);

    const filteredGames = useMemo(() => {
        if (!selectedMap) return myGames;
        return myGames.filter((g) => matchesSelectedMap(g, selectedMap));
    }, [myGames, selectedMap]);

    const dayGroups = useMemo(() => groupGamesByDay(myGames), [myGames]);
    const gameIdsKey = useMemo(() => myGames.map((g) => g.id).join(","), [myGames]);
    const filteredGameIds = useMemo(() => new Set(filteredGames.map((g) => g.id)), [filteredGames]);

    // Drop selection if that map disappears from the current period
    useEffect(() => {
        if (!selectedMap) return;
        const stillPresent = mapStats.some((m) => m.map.toLowerCase() === selectedMap.toLowerCase());
        if (!stillPresent) setSelectedMap(null);
    }, [mapStats, selectedMap]);

    const mapsById = useMemo(
        () => new Map(ctx.mapsReferential.map((m) => [m.id.toLowerCase(), m])),
        [ctx.mapsReferential],
    );

    useEffect(() => {
        if (!userTeam) {
            setSummaries([]);
            setStatsLoading(false);
            return;
        }

        setStatsLoading(true);
        ctx.reckonApi.scrimGameSummaries
            .list({
                ordering: ["-acs"],
                team1: userTeam,
                additional_filters: {
                    date__gte: from.toISOString(),
                    date__lt: until.toISOString(),
                },
            })
            .then((result) => {
                setSummaries(result);
                setStatsLoading(false);
            })
            .catch(() => setStatsLoading(false));
    }, [from, until, userTeam]);

    useEffect(() => {
        if (!userTeam) {
            setMapRoundRates({
                pistol: new Map(),
                atk: new Map(),
                def: new Map(),
            });
            return;
        }

        const dateFilters = {
            date__gte: from.toISOString(),
            date__lt: until.toISOString(),
        };
        const base = {
            team1: userTeam,
            groupby: ["map"],
            ordering: ["map"],
            additional_filters: dateFilters,
        };

        const fetchAgg = async (extra: Record<string, unknown> = {}) => {
            const request = { ...base, ...extra };
            try {
                return await ctx.reckonApi.scrimTeamRoundSummaries.aggregate({
                    ...request,
                    aggregates: ["id__cnt,win__avg"],
                });
            } catch {
                try {
                    return await ctx.reckonApi.scrimTeamRoundSummaries.aggregate({
                        ...request,
                        aggregates: ["win__avg"],
                    });
                } catch {
                    return [];
                }
            }
        };

        let cancelled = false;
        Promise.all([
            fetchAgg({
                additional_filters: { ...dateFilters, round_index__in: "1,13" },
            }),
            fetchAgg({ side: "Attacker" }),
            fetchAgg({ side: "Defender" }),
        ]).then(([pistolRows, atkRows, defRows]) => {
            if (cancelled) return;
            setMapRoundRates({
                pistol: mapRateStatsFromRows(pistolRows),
                atk: mapRateStatsFromRows(atkRows),
                def: mapRateStatsFromRows(defRows),
            });
        });

        return () => {
            cancelled = true;
        };
    }, [from, until, userTeam]);

    useEffect(() => {
        if (!gameIdsKey) {
            setGameBreakdowns(new Map());
            return;
        }

        let cancelled = false;
        ctx.reckonApi.scrimTeamRoundSummaries
            .list({
                team1: userTeam,
                ordering: ["round_index"],
                additional_filters: {
                    game__in: gameIdsKey,
                },
            })
            .then((rounds) => {
                if (cancelled) return;
                setGameBreakdowns(buildGameRoundBreakdowns(rounds, userTeam));
            })
            .catch(() => {
                if (cancelled) return;
                setGameBreakdowns(new Map());
            });

        return () => {
            cancelled = true;
        };
    }, [gameIdsKey, userTeam]);

    const players = useMemo(() => {
        const scopedSummaries = selectedMap
            ? summaries.filter((s) => filteredGameIds.has(s.game_id))
            : summaries;
        return aggregatePlayers(scopedSummaries, gamesById);
    }, [summaries, gamesById, selectedMap, filteredGameIds]);

    const loading = gamesLoading;

    return (
        <WindowContainer
            spacing={2}
            sx={{
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
                pb: 2,
            }}
        >
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{ alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", flexShrink: 0 }}
            >
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0, flex: 1 }}>
                    <TeamIcon team_name={userTeam} sx={{ width: 40, height: 40, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                            Scrim Dashboard
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {teamLabel(userTeam)} · overview of recent practice
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <DatePicker value={from} onChange={(v: Dayjs | null) => v && setFrom(v)} label="From" />
                    <DatePicker value={until} onChange={(v: Dayjs | null) => v && setUntil(v)} label="Until" />
                </Stack>
            </Stack>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
                    <CircularProgress sx={{ color: neonColor }} />
                </Box>
            ) : myGames.length === 0 ? (
                <NeonCard>
                    <NeonCardContent>
                        <Typography sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                            No scrim games found for {teamLabel(userTeam)} in this period.
                        </Typography>
                    </NeonCardContent>
                </NeonCard>
            ) : (
                <>
                    {/* Left: recent games + players. Right: map stats fills remaining height */}
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.4fr) minmax(0, 1fr)" },
                            gap: 2,
                            alignItems: "stretch",
                        }}
                    >
                        <Stack
                            spacing={2}
                            sx={{
                                minWidth: 0,
                                minHeight: 0,
                                height: "100%",
                                overflow: "hidden",
                            }}
                        >
                            <NeonCard
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    minHeight: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                }}
                            >
                                <NeonCardHeader
                                    title={
                                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
                                            <Box component="span">Recent games</Box>
                                            <AnimatePresence initial={false}>
                                                {selectedMap ? (
                                                    <Box
                                                        key={selectedMap}
                                                        component={motion.span}
                                                        initial={{ opacity: 0, y: -6 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: 6 }}
                                                        transition={MAP_FILTER_MOTION}
                                                        sx={{ whiteSpace: "nowrap" }}
                                                    >
                                                        · {capitalize(selectedMap)}
                                                    </Box>
                                                ) : null}
                                            </AnimatePresence>
                                        </Box>
                                    }
                                    sx={{ minHeight: "auto", py: 1.25, flexShrink: 0 }}
                                    action={
                                        <NeonButton
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                                            onClick={() => navigate("/scrims-history")}
                                            sx={{ fontSize: "0.7rem", px: 1.25, py: 0.25 }}
                                        >
                                            History
                                        </NeonButton>
                                    }
                                />
                                <NeonCardContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        pt: 0,
                                        px: 1.25,
                                        pb: "12px !important",
                                        flex: 1,
                                        minHeight: 0,
                                        ...scrollable,
                                        overflowY: "scroll",
                                    }}
                                >
                                    {dayGroups.map((group, groupIndex) => {
                                        const dayVisible = group.games.some((g) =>
                                            matchesSelectedMap(g, selectedMap),
                                        );
                                        return (
                                            <React.Fragment key={group.dayKey}>
                                                <FilterCollapse
                                                    in={dayVisible}
                                                    innerSx={groupIndex === 0 ? undefined : { pt: 0.5 }}
                                                >
                                                    <DayHeader
                                                        label={dayjs(group.games[0].date).format("ddd, MMM D")}
                                                        neonColor={neonColor}
                                                    />
                                                </FilterCollapse>
                                                {group.games.map((game) => (
                                                    <FilterCollapse
                                                        key={game.id}
                                                        in={matchesSelectedMap(game, selectedMap)}
                                                    >
                                                        <GameRow
                                                            game={game}
                                                            userTeam={userTeam}
                                                            mapsById={mapsById}
                                                            breakdown={gameBreakdowns.get(game.id)}
                                                        />
                                                    </FilterCollapse>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </NeonCardContent>
                            </NeonCard>

                            <NeonCard sx={{ height: "auto", minWidth: 0, flexShrink: 0, overflow: "hidden" }}>
                                <NeonCardHeader
                                    title={
                                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
                                            <Box component="span">Player stats</Box>
                                            <AnimatePresence initial={false}>
                                                {selectedMap ? (
                                                    <Box
                                                        key={selectedMap}
                                                        component={motion.span}
                                                        initial={{ opacity: 0, y: -6 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: 6 }}
                                                        transition={MAP_FILTER_MOTION}
                                                        sx={{ whiteSpace: "nowrap" }}
                                                    >
                                                        · {capitalize(selectedMap)}
                                                    </Box>
                                                ) : null}
                                            </AnimatePresence>
                                        </Box>
                                    }
                                    sx={{ minHeight: 52, py: 1.25, flexShrink: 0 }}
                                />
                                <NeonCardContent
                                    sx={{
                                        pt: 0,
                                        pb: "12px !important",
                                        width: "100%",
                                        minWidth: 0,
                                    }}
                                >
                                    {statsLoading ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                            <CircularProgress size={28} sx={{ color: neonColor }} />
                                        </Box>
                                    ) : (
                                        <PlayerStatsTable players={players} />
                                    )}
                                </NeonCardContent>
                            </NeonCard>
                        </Stack>

                        <Box
                            sx={{
                                minWidth: 0,
                                minHeight: { xs: 360, md: 0 },
                                height: { xs: 360, md: "100%" },
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <NeonCard
                                sx={{
                                    height: "100%",
                                    minWidth: 0,
                                    minHeight: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: 1,
                                    overflow: "hidden",
                                }}
                            >
                                <NeonCardHeader
                                    title="Map stats"
                                    sx={{ minHeight: "auto", py: 1.25, flexShrink: 0 }}
                                    action={
                                        <NeonButton
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                                            onClick={() => navigate("/scrims-map-stats")}
                                            sx={{ fontSize: "0.7rem", px: 1.25, py: 0.25 }}
                                        >
                                            Details
                                        </NeonButton>
                                    }
                                />
                                <NeonCardContent
                                    sx={{
                                        pt: 0,
                                        pb: "12px !important",
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        minHeight: 0,
                                        overflow: "hidden",
                                    }}
                                >
                                    {mapStats.length === 0 ? (
                                        <Typography sx={{ color: "rgba(255,255,255,0.55)", py: 3, textAlign: "center" }}>
                                            No maps played in this period.
                                        </Typography>
                                    ) : (
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: `repeat(${mapGrid.cols}, minmax(0, 1fr))`,
                                                gridTemplateRows: `repeat(${mapGrid.rows}, minmax(0, 1fr))`,
                                                gap: 0.75,
                                                flex: 1,
                                                minHeight: 0,
                                                height: "100%",
                                            }}
                                        >
                                            {mapStats.map((m) => (
                                                <MapStatCard
                                                    key={m.map}
                                                    stats={m}
                                                    neonColor={neonColor}
                                                    mapRef={mapsById.get(m.map.toLowerCase())}
                                                    selected={selectedMap?.toLowerCase() === m.map.toLowerCase()}
                                                    onSelect={() =>
                                                        setSelectedMap((prev) =>
                                                            prev?.toLowerCase() === m.map.toLowerCase()
                                                                ? null
                                                                : m.map,
                                                        )
                                                    }
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </NeonCardContent>
                            </NeonCard>
                        </Box>
                    </Box>
                </>
            )}
        </WindowContainer>
    );
}
